import {
  buildMetaFbc,
  getCapturedMarketingAttribution,
  type MarketingAttribution,
  readBrowserCookie,
} from "@/app/router/marketing-attribution";
import { trackEvent } from "@/domains/metrics/segment";
import { TrackedEvent } from "@/domains/metrics/tracked-event";
import { clientEnvModule } from "@/modules/client-env";

const ACCOUNT_SIGNUP_CONVERSION_WINDOW_MS = 24 * 60 * 60 * 1000;
const MAX_SERVER_CLOCK_SKEW_MS = 5 * 60 * 1000;
const ACCOUNT_SIGNUP_STORAGE_KEY_PREFIX = "mem.conversion.account_signup_completed";
const MOBILE_WEB_LOGIN_STORAGE_KEY_PREFIX = "mem.conversion.mobile_web_login_completed";
const MEM_CONVERSION_DATA_LAYER_EVENT = "mem_conversion";
const META_PAID_UTM_SOURCES = new Set([
  "facebook",
  "facebook_ads",
  "fb",
  "ig",
  "instagram",
  "instagram_ads",
  "meta",
  "meta_ads",
]);
const PAID_UTM_MEDIUMS = new Set(["cpc", "paid", "paid_social", "ppc", "social_paid"]);

export interface ConversionAttributionProperties {
  conversion_event_id: string;
  marketing_attribution_captured_at?: string;
  marketing_journey_id?: string;
  marketing_landing_path?: string;
  marketing_referrer_domain?: string;
  meta_fbc?: string;
  meta_fbclid?: string;
  meta_fbp?: string;
}

type AdvertisingConversionName =
  | TrackedEvent.AccountSignupCompleted
  | TrackedEvent.ProactiveAccessEnabled;

/**
 * Publishes canonical conversion milestones to the existing GTM data layer.
 *
 * Segment remains the source of truth. This browser-side bridge lets advertising
 * destinations subscribe to the same stable event names and deduplication IDs
 * without inferring conversions from routes or UI state.
 */
const pushAdvertisingConversion = ({
  conversionEventId,
  conversionName,
  conversionValueUsd,
}: {
  conversionEventId: string;
  conversionName: AdvertisingConversionName;
  conversionValueUsd?: number;
}): void => {
  const dataLayer = Reflect.get(window, "dataLayer");
  if (!Array.isArray(dataLayer)) {
    return;
  }

  dataLayer.push({
    event: MEM_CONVERSION_DATA_LAYER_EVENT,
    mem_conversion_event_id: conversionEventId,
    mem_conversion_name: conversionName,
    ...(conversionValueUsd !== undefined ? { mem_conversion_value_usd: conversionValueUsd } : {}),
  });
};

export const buildConversionAttributionProperties = ({
  conversionEventId,
}: {
  conversionEventId: string;
}): ConversionAttributionProperties => {
  const attribution = getCapturedMarketingAttribution();
  const metaFbc =
    (attribution?.fbc !== undefined || attribution?.fbclid !== undefined
      ? readBrowserCookie("_fbc")
      : undefined) ??
    attribution?.fbc ??
    buildMetaFbc({
      capturedAt: attribution?.capturedAt,
      fbclid: attribution?.fbclid,
    });

  return {
    conversion_event_id: conversionEventId,
    marketing_attribution_captured_at: attribution?.capturedAt,
    marketing_journey_id: attribution?.journeyId,
    marketing_landing_path: attribution?.landingPath,
    marketing_referrer_domain: attribution?.referrerDomain,
    meta_fbc: metaFbc,
    meta_fbclid: attribution?.fbclid,
    meta_fbp:
      attribution?.capturedAt !== undefined
        ? (readBrowserCookie("_fbp") ?? attribution.fbp)
        : undefined,
  };
};

export const accountSignupAgeSeconds = ({
  accountCreatedAt,
  now = Date.now(),
}: {
  accountCreatedAt: string | null;
  now?: number;
}): number | null => {
  if (accountCreatedAt === null) {
    return null;
  }

  const createdAt = Date.parse(accountCreatedAt);
  if (!Number.isFinite(createdAt)) {
    return null;
  }

  const ageMs = now - createdAt;
  if (ageMs < -MAX_SERVER_CLOCK_SKEW_MS || ageMs > ACCOUNT_SIGNUP_CONVERSION_WINDOW_MS) {
    return null;
  }
  return Math.max(0, Math.floor(ageMs / 1000));
};

const normalizeCampaignValue = (value: string | undefined): string =>
  value
    ?.trim()
    .toLowerCase()
    .replaceAll(/[\s-]+/g, "_") ?? "";

export const hasPaidMetaAttribution = (attribution: MarketingAttribution): boolean => {
  const utmSource = normalizeCampaignValue(attribution.utmParams.utm_source);
  const utmMedium = normalizeCampaignValue(attribution.utmParams.utm_medium);

  return META_PAID_UTM_SOURCES.has(utmSource) && PAID_UTM_MEDIUMS.has(utmMedium);
};

const accountSignupWasTracked = (spaceAccountId: string): boolean => {
  try {
    return (
      localStorage.getItem(`${ACCOUNT_SIGNUP_STORAGE_KEY_PREFIX}.${spaceAccountId}`) === "true"
    );
  } catch {
    return false;
  }
};

const markAccountSignupTracked = (spaceAccountId: string): void => {
  try {
    localStorage.setItem(`${ACCOUNT_SIGNUP_STORAGE_KEY_PREFIX}.${spaceAccountId}`, "true");
  } catch {
    // A stable conversion_event_id still lets Meta deduplicate storage-restricted retries.
  }
};

const mobileWebLoginStorageKey = ({
  journeyId,
  spaceAccountId,
}: {
  journeyId: string;
  spaceAccountId: string;
}): string => `${MOBILE_WEB_LOGIN_STORAGE_KEY_PREFIX}.${spaceAccountId}.${journeyId}`;

const mobileWebLoginWasTracked = ({
  journeyId,
  spaceAccountId,
}: {
  journeyId: string;
  spaceAccountId: string;
}): boolean => {
  try {
    return localStorage.getItem(mobileWebLoginStorageKey({ journeyId, spaceAccountId })) === "true";
  } catch {
    return false;
  }
};

const markMobileWebLoginTracked = ({
  journeyId,
  spaceAccountId,
}: {
  journeyId: string;
  spaceAccountId: string;
}): void => {
  try {
    localStorage.setItem(mobileWebLoginStorageKey({ journeyId, spaceAccountId }), "true");
  } catch {
    // A stable conversion_event_id still lets Meta deduplicate storage-restricted retries.
  }
};

/**
 * Emits the registration milestone after Segment identify has joined the
 * Market anonymous journey to the new personal space account.
 */
export const trackAccountSignupCompletedIfRecent = ({
  accountCreatedAt,
  accountEmail,
  spaceAccountId,
}: {
  accountCreatedAt: string | null;
  accountEmail: string;
  spaceAccountId: string;
}): void => {
  const accountAgeSeconds = accountSignupAgeSeconds({ accountCreatedAt });
  if (accountAgeSeconds === null || accountSignupWasTracked(spaceAccountId)) {
    return;
  }

  markAccountSignupTracked(spaceAccountId);
  const conversionEventId = `account-signup:${spaceAccountId}`;

  trackEvent(TrackedEvent.AccountSignupCompleted, {
    ...buildConversionAttributionProperties({
      conversionEventId,
    }),
    account_age_seconds: accountAgeSeconds,
    account_email: accountEmail,
  });
  pushAdvertisingConversion({
    conversionEventId,
    conversionName: TrackedEvent.AccountSignupCompleted,
  });
};

/**
 * Emits the authenticated mobile-web milestone after Segment identify has
 * joined the paid-Meta Market journey to the personal space account.
 */
export const trackMobileWebLoginCompleted = ({
  accountEmail,
  isIosBrowser = clientEnvModule.isIosBrowser(),
  spaceAccountId,
}: {
  accountEmail: string;
  isIosBrowser?: boolean;
  spaceAccountId: string;
}): void => {
  const attribution = getCapturedMarketingAttribution();
  const journeyId = attribution?.journeyId;
  if (
    !isIosBrowser ||
    attribution === null ||
    journeyId === undefined ||
    !hasPaidMetaAttribution(attribution) ||
    mobileWebLoginWasTracked({ journeyId, spaceAccountId })
  ) {
    return;
  }

  markMobileWebLoginTracked({ journeyId, spaceAccountId });
  trackEvent(TrackedEvent.MobileWebLoginCompleted, {
    ...buildConversionAttributionProperties({
      conversionEventId: `mobile-web-login:${spaceAccountId}:${journeyId}`,
    }),
    account_email: accountEmail,
  });
};

export const trackMemAgentMessageSent = ({
  inputMethod,
  sessionEventId,
}: {
  inputMethod: "text" | "voice";
  sessionEventId: string;
}): void => {
  trackEvent(TrackedEvent.MemAgentMessageSent, {
    ...buildConversionAttributionProperties({
      conversionEventId: `mem-agent-message:${sessionEventId}`,
    }),
    channel: "native_chat",
    input_method: inputMethod,
  });
};

export const trackProactiveAccessEnabled = ({
  billingProvider,
  checkoutSource,
  currentPeriodStart,
  planVariant,
  pricingExperimentKey,
  pricingExperimentVariantKey,
  providerSubscriptionId,
  spaceAccountId,
  stripeSessionId,
  subscriptionPriceUsd,
}: {
  billingProvider: string | null;
  checkoutSource: "existing_user_onboarding" | "onboarding" | "upgrade_plan" | "upsell";
  currentPeriodStart?: string;
  planVariant: string | null;
  pricingExperimentKey?: string;
  pricingExperimentVariantKey?: string;
  providerSubscriptionId: string | null;
  spaceAccountId: string;
  stripeSessionId?: string | null;
  subscriptionPriceUsd?: number;
}): void => {
  const accessIdentity =
    billingProvider !== null && providerSubscriptionId !== null
      ? `${billingProvider}:${providerSubscriptionId}`
      : (stripeSessionId ?? currentPeriodStart ?? spaceAccountId);
  const conversionEventId = `proactive-access:${spaceAccountId}:${accessIdentity}`;

  trackEvent(TrackedEvent.ProactiveAccessEnabled, {
    ...buildConversionAttributionProperties({
      conversionEventId,
    }),
    billing_provider: billingProvider ?? undefined,
    checkout_source: checkoutSource,
    plan_kind: "proactive",
    plan_variant: planVariant ?? undefined,
    pricing_experiment_key: pricingExperimentKey,
    pricing_experiment_variant_key: pricingExperimentVariantKey,
    provider_subscription_id: providerSubscriptionId ?? undefined,
    subscription_price_usd: subscriptionPriceUsd,
  });
  pushAdvertisingConversion({
    conversionEventId,
    conversionName: TrackedEvent.ProactiveAccessEnabled,
    conversionValueUsd: subscriptionPriceUsd,
  });
};
